import { Form, FormCheckProps } from "react-bootstrap";
import { BaseInputProps } from "./BaseInputProps";
import { usePermissions } from "../../../contexts";
import React, { Fragment, ReactNode, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { ErrorMessage } from ".";
import { getDisplayedAccessor, getFormError } from "../../../helper-functions";

export interface RadioOption {
    label: ReactNode;
    value: string;
    name?: string;
    id?: string;
    ariaLabel?: string;
}

export interface RadioGroupProps extends BaseInputProps, Omit<FormCheckProps, 'onChange'> {
    options: RadioOption[];
    groupName?: string;
    inlineGroupName?: boolean;
    required?: boolean;
    onChange?: (value: string) => void;

}

export function RadioGroup({
    accessor,
    viewClaim,
    editClaim,
    readOnly,
    className,
    suppressMargin,
    options,
    groupName,
    title,
    "aria-label": ariaLabel,
    inlineGroupName,
    required,
    onChange,
    ...rest
}: Readonly<RadioGroupProps>) {
    const {
        register,
        formState: { errors },
    } = useFormContext();
    const { hasPermission } = usePermissions();
    const error = useMemo(() => getFormError(errors, accessor), [errors, accessor]);

    const isReadOnly = useMemo(
        () => (editClaim ? !hasPermission(editClaim) : readOnly),
        [readOnly, editClaim, hasPermission]
    );

    const getOptionId = (option: RadioOption) => {
        if (option.id) return `${accessor}-${option.id}`;
        return `${accessor}-${option.value}`;
    };

    if (!!viewClaim && !hasPermission(viewClaim)) return null;

    return (
        <Form.Group
            controlId={accessor}
            className={`${!suppressMargin && "mb-2"} ${className}`}
            aria-label={ariaLabel ?? groupName}
        >
            {!!groupName &&
                (inlineGroupName ? (
                    <Form.Label className="me-2">
                        {groupName} {required && <span className="text-danger">*</span>}
                    </Form.Label>
                ) : (
                    <div>
                        <Form.Label>
                            {groupName} {required && <span className="text-danger">*</span>}
                        </Form.Label>
                    </div>
                ))}
            {options.map((option) => (
                <Form.Check
                    key={`${accessor}-${option.value}`}
                    type="radio"
                    value={option.value}

                    id={getOptionId(option)}
                    readOnly={isReadOnly}
                    aria-readonly={isReadOnly}
                    aria-label={option.ariaLabel ?? option.label?.toString()}
                    className={`${!suppressMargin && "mb-2"} ${className}`}
                    title={title ?? getDisplayedAccessor(accessor)}
                    {...rest}
                    label={option.label}
                    {...register(accessor, {
                        onChange: (e) => {
                            const value = e.target.value;
                            onChange?.(value);
                        }
                    })}
                />
            ))}
            <ErrorMessage error={error} />
        </Form.Group>
    );
}
